import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageSquare, X } from 'lucide-react';
import type { UnreadJob } from '../hooks/useGlobalChat';

interface GlobalChatToastProps {
  notification: UnreadJob | null;
  onDismiss: () => void;
}

export default function GlobalChatToast({ notification, onDismiss }: GlobalChatToastProps) {
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);
  const [exiting, setExiting] = useState(false);

  useEffect(() => {
    if (notification) {
      setVisible(true);
      setExiting(false);

      // Auto-dismiss after 5 seconds
      const timer = setTimeout(() => {
        dismiss();
      }, 5000);

      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notification]);

  const dismiss = () => {
    setExiting(true);
    setTimeout(() => {
      setVisible(false);
      setExiting(false);
      onDismiss();
    }, 300);
  };

  const handleClick = () => {
    if (notification) {
      navigate(`/jobs/${notification.jobId}`);
    }
    dismiss();
  };

  if (!visible || !notification) return null;

  const truncatedMessage =
    notification.latestMessage && notification.latestMessage.length > 60
      ? notification.latestMessage.substring(0, 60) + '...'
      : notification.latestMessage || '';

  return (
    <>
      <div
        className={`fixed top-4 right-4 z-50 max-w-sm w-full cursor-pointer ${
          exiting ? 'animate-slide-out-right' : 'animate-slide-in-right'
        }`}
        onClick={handleClick}
      >
        <div className="bg-fieldiq-card border border-fieldiq-border border-l-4 border-l-fieldiq-accent rounded-xl p-3 shadow-2xl">
          {/* Header */}
          <div className="flex items-center justify-between mb-1.5">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-full bg-fieldiq-accent flex items-center justify-center flex-shrink-0">
                <MessageSquare size={14} className="text-white" />
              </div>
              <div>
                <div className="text-sm font-semibold text-white">
                  {notification.senderName}
                </div>
                <div className="text-xs text-fieldiq-muted">
                  Job {notification.jobNumber}
                  {notification.unreadCount > 1 && (
                    <span className="ml-1.5 inline-flex items-center justify-center px-1.5 py-0.5 text-[10px] font-bold bg-fieldiq-accent text-white rounded-full">
                      {notification.unreadCount}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                dismiss();
              }}
              className="text-gray-500 hover:text-white transition-colors p-1"
            >
              <X size={14} />
            </button>
          </div>
          {/* Message preview */}
          <div className="text-sm text-fieldiq-text leading-snug pl-9">
            {truncatedMessage}
          </div>
        </div>
      </div>

      {/* Animation keyframes */}
      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(110%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        @keyframes slideOutRight {
          from { transform: translateX(0); opacity: 1; }
          to { transform: translateX(110%); opacity: 0; }
        }
        .animate-slide-in-right {
          animation: slideInRight 0.3s ease-out forwards;
        }
        .animate-slide-out-right {
          animation: slideOutRight 0.3s ease-in forwards;
        }
      `}</style>
    </>
  );
}
